import styled, { css } from "styled-components/macro";
import useFlagsWithDefaults, {
  editableFeatureFlagsAtom,
  FeatureFlags,
  FEATURE_FLAG_DESCRIPTIONS,
} from "utils/customHooks/useFlagsWithDefaults";
import { objectFromEntries } from "@raven/shared/utils";
import {
  Collapse,
  IconButton,
  InputLabel,
  List,
  MenuItem,
  Select,
} from "@mui/material";
import { ListItemToggle } from "components/Settings/ListItemToggle";
import {
  darkGrey,
  flDanger60,
  flSuccess60,
  lighterGrey,
  medDarkGrey,
  lightGrey,
} from "constants/colors";
import { kebabCase, startCase } from "lodash";
import {
  Check,
  ChevronRight,
  Clear,
  Close,
  OpenInNew,
} from "@mui/icons-material";
import { TextField } from "@mui/material";
import { useState, useMemo } from "react";
import { useAtom } from "jotai";
import { useIsDarkMode } from "store/atoms";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useLocalStorage, useMount } from "react-use";
import { CustomJsonEditor } from "./CustomJsonEditor";

export function useEditableFlags() {
  return useAtom(editableFeatureFlagsAtom);
}
const INITIAL_COLLAPSED = {
  boolean: false,
  number: false,
  complex: false,
  selectable: false,
};
const CUSTOM_INTERNAL_FLAGS: [string, boolean][] = [
  ["flightDontAutoRedirectIntoGroupTile", false],
];
export function FlagsEditor() {
  const flags = useFlagsWithDefaults();
  useMount(() => {
    console.log("🚩 Flags:", flags);
  });
  const [, setEditableFlags] = useEditableFlags();
  const booleanFlags = useMemo(
    () => [
      ...CUSTOM_INTERNAL_FLAGS,
      ...Object.entries(flags).filter(
        ([key, val]) => val === true || val === false
      ),
    ],
    [flags]
  );
  const numberFlags = useMemo(
    () => Object.entries(flags).filter(([key, val]) => typeof val === "number"),
    [flags]
  );
  const [complexFlags, setComplexFlags] = useState(getComplexFlags(flags));
  const [editedJson, setEditedJson] = useState(objectFromEntries(complexFlags));
  const [searchTerm, setSearchTerm] = useState("");
  const [isDarkMode] = useIsDarkMode();
  const edited =
    JSON.stringify(objectFromEntries(complexFlags)) !==
    JSON.stringify(editedJson);

  const [collapsedLs, setCollapsed] = useLocalStorage(
    "debugger:flags:collapsed",
    INITIAL_COLLAPSED
  );
  const collapsed = useMemo(() => {
    return {
      ...INITIAL_COLLAPSED,
      ...collapsedLs,
    };
  }, [collapsedLs]);

  const numberFlagsSorted = useMemo(
    () =>
      numberFlags
        .sort((a, b) => (a[0] > b[0] ? 1 : -1))
        .filter(
          ([key, val]) =>
            !searchTerm ||
            searchTerm.split(" ").reduce<boolean>((acc, curr) => {
              return acc && key.toLowerCase().includes(curr.toLowerCase());
            }, true)
        ),
    [numberFlags, searchTerm]
  );
  const selectableFlags = useMemo(
    () =>
      Object.entries(SELECTABLE_FLAG_OPTIONS).filter(
        ([key, val]) =>
          !searchTerm ||
          searchTerm.split(" ").reduce<boolean>((acc, curr) => {
            return acc && key.toLowerCase().includes(curr.toLowerCase());
          }, true)
      ),
    [searchTerm]
  );
  const booleanFlagsFiltered = useMemo(
    () =>
      booleanFlags
        .sort((a, b) => (a[0] > b[0] ? 1 : -1))
        .filter(
          ([key, val]) =>
            !searchTerm || key.toLowerCase().includes(searchTerm.toLowerCase())
        ),

    [booleanFlags, searchTerm]
  );

  return (
    <FlagsEditorStyles $isDarkMode={isDarkMode}>
      <div className="topRow">
        <TextField
          variant="outlined"
          type="text"
          size="small"
          inputProps={{
            autoFocus: true,
          }}
          value={searchTerm}
          onChange={event => {
            setSearchTerm(event.target.value);
          }}
          label={<div>🔍 Search flags...</div>}
          InputProps={{
            endAdornment: searchTerm ? (
              <IconButton
                style={{ marginRight: -12 }}
                onClick={() => setSearchTerm("")}
              >
                <Clear />
              </IconButton>
            ) : null,
          }}
        />
        <a
          href={`https://app.clickup.com/14117868/v/dc/deuzc-13747/deuzc-5087`}
          target="_blank"
          rel="noopener noreferrer"
        >
          📚 Check out the docs ➡
        </a>
      </div>
      <h2
        onClick={() =>
          setCollapsed({ ...collapsed, boolean: !collapsed.boolean })
        }
      >
        <ChevronRight
          style={{
            transform: `rotate(${collapsed.boolean ? 0 : 0.25}turn)`,
          }}
        />{" "}
        Boolean flags
      </h2>
      <Collapse unmountOnExit={true} in={!collapsed.boolean}>
        <List>
          {booleanFlagsFiltered.map(([key, val]) => {
            const isInternalFlag = CUSTOM_INTERNAL_FLAGS.some(
              ([k, v]) => k === key
            );
            const internalFlagInitialValue = isInternalFlag
              ? window.localStorage.getItem(`internal-flag:${key}`) === "true"
              : false;
            return (
              <div key={key} className="row">
                <div className="column">
                  <ListItemToggle
                    style={{ transform: "scale(0.9)" }}
                    className="toggleSwitchSmall"
                    {...(isInternalFlag
                      ? { defaultChecked: internalFlagInitialValue }
                      : { checked: Boolean(val) })}
                    onChange={() => {
                      if (isInternalFlag) {
                        console.log(
                          `🚩 Custom internal flag, toggling localStorage value: internal-flag:${key}`
                        );
                        const newValue = !internalFlagInitialValue;
                        window.localStorage.setItem(
                          `internal-flag:${key}`,
                          newValue.toString()
                        );
                        return;
                      }
                      const newFlags = { ...flags, [key]: !val };
                      setEditableFlags(newFlags);
                    }}
                    label={startCase(key).replace("Flight ", "")}
                  />
                  <div className="description">
                    {FEATURE_FLAG_DESCRIPTIONS[key] ?? "[No description]"}
                  </div>
                </div>
                <a
                  href={`https://app.launchdarkly.com/default/production/features/${kebabCase(
                    key
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconButton size="large">
                    <OpenInNew />
                  </IconButton>
                </a>
              </div>
            );
          })}
        </List>
      </Collapse>
      <h2
        onClick={() =>
          setCollapsed({ ...collapsed, number: !collapsed.number })
        }
      >
        <ChevronRight
          style={{
            transform: `rotate(${collapsed.number ? 0 : 0.25}turn)`,
          }}
        />{" "}
        Number flags
      </h2>
      <Collapse unmountOnExit={true} in={!collapsed.number}>
        <List style={{ paddingTop: 16 }}>
          {numberFlagsSorted.map(([key, val]) => {
            return (
              <div key={key} className="row">
                <div className="column numberFlag">
                  <TextField
                    style={{ paddingBottom: 24 }}
                    variant="outlined"
                    type="number"
                    size="small"
                    value={val}
                    onChange={event => {
                      const newFlags = {
                        ...flags,
                        [key]: Number(event.target.value),
                      };
                      setEditableFlags(newFlags);
                    }}
                    label={startCase(key).replace("Flight ", "")}
                  />
                  <div className="description">
                    {FEATURE_FLAG_DESCRIPTIONS[key] ?? "[No description]"}
                  </div>
                </div>
                <a
                  href={`https://app.launchdarkly.com/default/production/features/${kebabCase(
                    key
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginTop: -26 }}
                >
                  <IconButton size="large">
                    <OpenInNew />
                  </IconButton>
                </a>
              </div>
            );
          })}
        </List>
      </Collapse>
      <h2
        onClick={() =>
          setCollapsed({ ...collapsed, selectable: !collapsed.selectable })
        }
      >
        <ChevronRight
          style={{
            transform: `rotate(${collapsed.selectable ? 0 : 0.25}turn)`,
          }}
        />{" "}
        Selectable flags
      </h2>
      <Collapse unmountOnExit={true} in={!collapsed.selectable}>
        <List style={{ display: "grid", marginTop: -8 }}>
          {selectableFlags.map(([key, val]) => {
            return (
              <div key={key} className="column selectableFlag">
                <SelectableFlagDropdown flagKey={key} flagOptions={val} />
                <div className="description">
                  {FEATURE_FLAG_DESCRIPTIONS[key] ?? "[No description]"}
                </div>
              </div>
            );
          })}
        </List>
      </Collapse>
      <h2
        onClick={() =>
          setCollapsed({ ...collapsed, complex: !collapsed.complex })
        }
      >
        <ChevronRight
          style={{
            transform: `rotate(${collapsed.complex ? 0 : 0.25}turn)`,
          }}
        />{" "}
        Complex flags
      </h2>
      <Collapse unmountOnExit={true} in={!collapsed.complex}>
        <ErrorBoundary id="FlagsEditor">
          <ComplexFlagsEditorStyles $isDarkMode={isDarkMode}>
            <CustomJsonEditor
              htmlElementProps={{
                style: { height: "100%" },
              }}
              onChange={newJson => {
                setEditedJson(newJson);
              }}
              value={editedJson}
              mode="code"
              allowedModes={["code", "tree"]}
            />
            <IconButton
              className="btnReset"
              disabled={!edited}
              onClick={() => {
                setEditedJson(objectFromEntries(complexFlags));
              }}
            >
              <Close />
            </IconButton>
            <IconButton
              className="btnSubmit"
              disabled={!edited}
              onClick={() => {
                const newComplexFlags = Object.entries(editedJson);
                setComplexFlags(newComplexFlags);
                const newFlags = { ...flags, ...editedJson };
                setEditableFlags(newFlags);
              }}
            >
              <Check />
            </IconButton>
          </ComplexFlagsEditorStyles>
        </ErrorBoundary>
      </Collapse>
    </FlagsEditorStyles>
  );
}
const SELECTABLE_FLAG_OPTIONS: Partial<Record<keyof FeatureFlags, string[]>> = {
  flightOverrideIsMidgardEnabled: ["enabled", "disabled", "default"],
  flightLinkToSightAnalytics: ["trial", "enabled", "hidden"],
};

function SelectableFlagDropdown({
  flagKey,
  flagOptions,
}: {
  flagKey: string;
  flagOptions: string[];
}) {
  const [editableFlags, setEditableFlags] = useEditableFlags();
  const label = startCase(flagKey).replace("Flight ", "");
  const value =
    editableFlags[flagKey] ??
    Object.entries(SELECTABLE_FLAG_OPTIONS).find(
      ([key, val]) => key === flagKey
    )?.[1][0] ??
    "default";
  return (
    <>
      <SelectableFlagDropdownStyles>
        <div style={{ flexGrow: 1 }}>
          <InputLabel id={label}>{label}</InputLabel>
          <Select
            size="small"
            label={label}
            labelId={label}
            value={value}
            onChange={event => {
              const newEditableFlags = {
                ...editableFlags,
                [flagKey]: event.target.value,
              };
              setEditableFlags(newEditableFlags);
            }}
            MenuProps={{
              style: { zIndex: 99999999999 },
            }}
          >
            {flagOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </div>
        <a
          href={`https://app.launchdarkly.com/default/production/features/${kebabCase(
            flagKey
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginBottom: -16 }}
        >
          <IconButton size="large">
            <OpenInNew />
          </IconButton>
        </a>
      </SelectableFlagDropdownStyles>
    </>
  );
}
const SelectableFlagDropdownStyles = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  transform: scale(0.9);
  .MuiSelect-select {
  }
  .MuiInputBase-root {
    width: 100%;
  }
`;
function getComplexFlags(flags) {
  return Object.entries(flags)
    .filter(([key, val]) => !Object.keys(SELECTABLE_FLAG_OPTIONS).includes(key))
    .filter(
      ([key, val]) => val !== true && val !== false && typeof val !== "number"
    );
}
const ComplexFlagsEditorStyles = styled.div<{ $isDarkMode: boolean }>`
  height: 500px;
  position: relative;
  .btnSubmit {
    position: absolute;
    bottom: 0;
    right: 32px;
  }
  .btnReset {
    position: absolute;
    bottom: 0;
    right: 64px;
    opacity: 0.5;
  }
  ${({ $isDarkMode }) =>
    $isDarkMode
      ? css`
          .btnSubmit:not(:disabled) {
            color: ${flSuccess60};
          }
          .btnReset:not(:disabled) {
            color: ${flDanger60};
          }
        `
      : css`
          .btnSubmit:not(:disabled) {
            color: #558d4f;
          }
          .btnReset:not(:disabled) {
            color: #9a2323;
          }
        `}
`;
export const FlagsEditorStyles = styled.div<{ $isDarkMode: boolean }>`
  .topRow {
    display: grid;
    grid-auto-flow: column;
    grid-gap: 24px;
    justify-content: space-between;
    align-items: center;
    margin: -12px 12px 12px 0;
  }
  h2 {
    font-size: 1.2em;
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    padding: 0.33em 32px;
    margin: 12px -32px;
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? medDarkGrey : lighterGrey};
    &:hover {
      background: ${({ $isDarkMode }) => ($isDarkMode ? darkGrey : lightGrey)};
    }
    .MuiSvgIcon-root {
      margin-right: 0.25em;
    }
  }
  .row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-gap: 10px;
    align-items: center;
    margin: 0 0 0.5em;
    padding: 0 0 0 12px;
  }
  .column {
    display: grid;
    grid-template-rows: 1fr auto;
  }
  .column.numberFlag,
  .column.selectableFlag {
    .MuiFormControl-root {
      padding-bottom: 6px !important;
    }
    padding-bottom: 16px;
  }
  .description {
    font-size: 0.75em;
    line-height: 1em;
    opacity: 0.8;
    text-indent: 2em;
  }
  .shrink {
    transform: scale(0.9);
  }
  .MuiTypography-root {
    line-height: 1em;
  }
  .toggleSwitchSmall {
    padding: 0 0 0 8px;
    overflow-wrap: anywhere;
    &:hover {
      background: ${({ $isDarkMode }) => ($isDarkMode ? darkGrey : lightGrey)};
    }
  }
`;
